import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button, Form, Card, Alert, Badge, Modal } from 'react-bootstrap';
import Select, { components } from 'react-select';
import { workoutAPI, withPerson } from '../services/workoutAPI';
import SetLogger from './SetLogger';
import CardioLogger from './CardioLogger';
import RestBlockCard from './RestBlockCard';
import ExerciseForm from './ExerciseForm';
import { useWorkoutDraft } from '../hooks/useWorkoutDraft';
import { usesSets, isStretch, hasReps, hasDuration } from '../utils/exerciseTypes';

function WorkoutLogger({ template, programWorkout, initialData, isEditMode, onSaveWorkout, onFinish, onCancel, userId }) {
  const [workoutName, setWorkoutName] = useState('');
  const [workoutDate, setWorkoutDate] = useState(new Date().toISOString().split('T')[0]);
  const [workoutTime, setWorkoutTime] = useState(new Date().toTimeString().split(' ')[0].substring(0, 5));
  const [workoutNotes, setWorkoutNotes] = useState('');
  const [durationHours, setDurationHours] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('');
  const [durationSeconds, setDurationSeconds] = useState('');

  // Program context - local state to support draft save/restore
  const [programContext, setProgramContext] = useState(null);

  // Workout state
  const [exercises, setExercises] = useState([]);
  const [workoutExercises, setWorkoutExercises] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);


  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [showExerciseForm, setShowExerciseForm] = useState(false);
  const [isSavingExercise, setIsSavingExercise] = useState(false);

  // Drag-and-drop state for reordering
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Superset creation state
  const [selectedExerciseIds, setSelectedExerciseIds] = useState(new Set());
  const [showSupersetModal, setShowSupersetModal] = useState(false);

  // Draft persistence
  const { hasDraft, draftAge, checkDraft, loadDraft, saveDraft, saveDraftImmediately, clearDraft } = useWorkoutDraft(userId);
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const isInitialMount = useRef(true);

  // Notes modal state
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesModalData, setNotesModalData] = useState({ exerciseId: null, supersetId: null, subIndex: null, notes: '', template_notes: '', exerciseName: '', exercise_id: null });
  const [previousNote, setPreviousNote] = useState(null);
  const [loadingPreviousNote, setLoadingPreviousNote] = useState(false);

  // Template/Program notes modal state
  const [showTemplateNotesModal, setShowTemplateNotesModal] = useState(false);
  const [showProgramNotesModal, setShowProgramNotesModal] = useState(false);

  // Substitute exercise modal state
  const [showSubstituteModal, setShowSubstituteModal] = useState(false);
  const [substituteTarget, setSubstituteTarget] = useState(null); // { exerciseId, supersetId, subIndex, exercise_type, exercise_id }
  const [substituteSelection, setSubstituteSelection] = useState(null);

  // Helper function to apply cardio overrides to template exercises
  const applyCardioOverrides = useCallback((template, overrides) => {
    if (!overrides || !template?.exercises) return template;

    return {
      ...template,
      exercises: template.exercises.map(ex => {
        const overrideKey = `exercise_id_${ex.exercise_id}`;
        const override = overrides[overrideKey];

        if (!override) return ex;

        // Apply override for basic cardio
        if (ex.exercise_type === 'cardio_basic' && override.distance !== undefined) {
          return {
            ...ex,
            cardio_data: {
              distance: override.distance,
              distance_unit: override.distance_unit,
              duration_seconds: override.duration_seconds,
              notes: override.notes || (ex.cardio_data ? ex.cardio_data.notes : null)
            }
          };
        }

        // Apply override for segmented cardio
        if (ex.exercise_type === 'cardio_segments' && override.segments) {
          return {
            ...ex,
            cardio_segments: override.segments
          };
        }

        return ex;
      })
    };
  }, []);

  useEffect(() => {
    const initializeComponent = async () => {
      await fetchExercises();

      console.log('🔍 WorkoutLogger initialized with:', { template, programWorkout, initialData, isEditMode });

      // Initialize program context from prop
      if (programWorkout) {
        console.log('🔗 Initializing program context from prop:', programWorkout);
        setProgramContext(programWorkout);
      }

      // If initialData provided (edit mode), load that first
      if (initialData && isEditMode) {
        console.log('✏️ Edit mode - loading initial data:', initialData);
        setWorkoutName(initialData.workoutName || '');
        setWorkoutDate(initialData.workoutDate || new Date().toISOString().split('T')[0]);
        setWorkoutTime(initialData.workoutTime || '');
        setWorkoutNotes(initialData.workoutNotes || '');
        // Convert stored duration (in minutes, possibly decimal) to HH:MM:SS
        if (initialData.durationMinutes) {
          const totalMins = parseFloat(initialData.durationMinutes) || 0;
          const totalSeconds = Math.round(totalMins * 60);
          const hrs = Math.floor(totalSeconds / 3600);
          const mins = Math.floor((totalSeconds % 3600) / 60);
          const secs = totalSeconds % 60;
          setDurationHours(hrs > 0 ? hrs : '');
          setDurationMinutes(mins > 0 ? mins : '');
          setDurationSeconds(secs > 0 ? secs : '');
        }

        if (initialData.exercises && initialData.exercises.length > 0) {
          setWorkoutExercises(initialData.exercises);
        }

        // Mark initialization complete
        isInitialMount.current = false;
        return; // Skip template and draft loading in edit mode
      }

      // If programWorkout provided, use its template and name
      // Note: Use programWorkout prop directly, not programContext state (async update hasn't completed yet)
      let effectiveTemplate = programWorkout?.template || template;
      const effectiveName = programWorkout?.name || template?.name || '';

      console.log('📝 Effective template:', effectiveTemplate);
      console.log('🎯 Program workout cardio_overrides:', programWorkout?.cardio_overrides);

      // Apply cardio overrides if present
      if (programWorkout?.cardio_overrides && effectiveTemplate) {
        console.log('🏃 Applying cardio overrides:', programWorkout.cardio_overrides);
        console.log('📋 Template before overrides:', JSON.stringify(effectiveTemplate.exercises?.[0]?.cardio_data));
        effectiveTemplate = applyCardioOverrides(effectiveTemplate, programWorkout.cardio_overrides);
        console.log('✅ Template after overrides:', JSON.stringify(effectiveTemplate.exercises?.[0]?.cardio_data));
      } else {
        console.log('❌ NOT applying overrides - programWorkout.cardio_overrides:', programWorkout?.cardio_overrides, 'effectiveTemplate:', !!effectiveTemplate);
      }

      // Check for draft FIRST (unless template or programWorkout provided)
      if (!effectiveTemplate && !programWorkout) {
        const draft = await loadDraft();
        console.log('🔍 Checking for draft on mount:', draft);

        if (draft) {
          console.log('📋 Draft found! Showing prompt to resume...');
          setShowDraftPrompt(true);
        } else {
          console.log('🆕 No draft found, starting fresh workout');
        }
      } else if (effectiveTemplate) {
        // If template or programContext provided, populate workout
        console.log('📝 Template provided, loading template:', effectiveTemplate);
        setWorkoutName(effectiveName);

        // Load template exercises with all data (sets, cardio, supersets, rest blocks)
        if (effectiveTemplate.exercises && effectiveTemplate.exercises.length > 0) {
          const templateExercises = effectiveTemplate.exercises.map((ex, index) => {
            // Convert 'rest_block' to 'rest' for internal consistency
            const itemType = ex.item_type === 'rest_block' ? 'rest' : (ex.item_type || 'exercise');

            const item = {
              type: itemType,
              id: Date.now() + index, // Temporary ID for frontend
              exercise_id: ex.exercise_id,
              exercise_name: ex.exercise_name || ex.name,
              exercise_type: ex.exercise_type,
              category: ex.category,
              template_notes: ex.notes || '', // Read-only template notes
              notes: '', // User's session notes (starts empty)
              superset_group_id: ex.superset_group_id || null
            };

            // Add rest block data if this is a rest block
            if (ex.item_type === 'rest_block') {
              item.rest_duration_seconds = ex.rest_duration_seconds || 60;
              item.timer_running = false;
              item.elapsed_seconds = 0;
            } else {
              // For exercises, load sets or cardio data from template
              const exerciseUsesSets = usesSets(ex.exercise_type);

              if (exerciseUsesSets) {
                // Load pre-filled sets from template, or create one empty set
                // Copy reps_prescription from template for display, but clear reps for user to fill
                item.sets = ex.sets && ex.sets.length > 0 ? ex.sets.map(set => ({
                  ...set,
                  id: Date.now() + Math.random(), // Add unique ID for frontend
                  weight: set.weight || '',
                  // Keep reps_prescription for display guidance, clear reps for user input
                  reps_prescription: set.reps_prescription || (set.reps ? String(set.reps) : ''),
                  reps: '', // User fills in actual reps performed
                  duration_seconds: set.duration_seconds || '',
                  notes: set.notes || ''
                })) : [{
                  set_number: 1,
                  weight: '',
                  weight_unit: 'lbs',
                  resistance: isStretch(ex.exercise_type) ? '' : undefined,
                  reps: hasReps(ex.exercise_type) ? '' : undefined,
                  reps_prescription: undefined,
                  duration_seconds: hasDuration(ex.exercise_type) ? '' : undefined,
                  notes: ''
                }];
              } else if (ex.exercise_type === 'cardio_basic') {
                // Load cardio data from template
                item.cardio_data = ex.cardio_data || null;
              } else if (ex.exercise_type === 'cardio_segments') {
                // Load cardio segments from template
                item.cardio_segments = ex.cardio_segments || [];
              }
            }

            return item;
          });

          // Group exercises by superset_group_id
          const supersetGroups = new Map();
          const standaloneExercises = [];

          templateExercises.forEach(item => {
            if (item.superset_group_id) {
              if (!supersetGroups.has(item.superset_group_id)) {
                supersetGroups.set(item.superset_group_id, []);
              }
              supersetGroups.get(item.superset_group_id).push(item);
            } else {
              standaloneExercises.push(item);
            }
          });

          // Build final array with supersets and standalone exercises in original order
          const finalExercises = [];
          const processedSupersets = new Set();

          templateExercises.forEach(item => {
            if (item.superset_group_id && !processedSupersets.has(item.superset_group_id)) {
              // Create superset object
              const supersetExercises = supersetGroups.get(item.superset_group_id);
              const supersetName = `Superset: ${supersetExercises.map(ex =>
                ex.type === 'rest' ? 'Rest' : ex.exercise_name
              ).join(' + ')}`;

              finalExercises.push({
                type: 'superset',
                id: Date.now() + Math.random(),
                superset_name: supersetName,
                exercises: supersetExercises
              });
              processedSupersets.add(item.superset_group_id);
            } else if (!item.superset_group_id) {
              // Add standalone exercise or rest block
              finalExercises.push(item);
            }
          });

          setWorkoutExercises(finalExercises);
          console.log(`✅ Loaded ${finalExercises.length} items from template (including sets, cardio, supersets, rest blocks)`);
        }
      }

      // Mark initialization complete after loading
      isInitialMount.current = false;
    };

    initializeComponent();
  }, [template, programWorkout, initialData, isEditMode, loadDraft]);


  // Auto-sync workout duration from single cardio exercise
  useEffect(() => {
    // Skip during initialization
    if (isInitialMount.current) return;

    // Get all actual exercises (not rest blocks, flatten supersets)
    const allExercises = [];
    workoutExercises.forEach(item => {
      if (item.type === 'superset') {
        item.exercises.forEach(ex => {
          if (ex.type !== 'rest') allExercises.push(ex);
        });
      } else if (item.type !== 'rest') {
        allExercises.push(item);
      }
    });

    // Only auto-sync if there's exactly one cardio exercise
    if (allExercises.length !== 1) return;

    const exercise = allExercises[0];
    let totalSeconds = 0;

    if (exercise.exercise_type === 'cardio_basic' && exercise.cardio_data?.duration_seconds) {
      totalSeconds = exercise.cardio_data.duration_seconds;
    } else if (exercise.exercise_type === 'cardio_segments' && exercise.cardio_segments?.length > 0) {
      // Sum up all segment durations
      totalSeconds = exercise.cardio_segments.reduce((sum, seg) => sum + (seg.duration_seconds || 0), 0);
    }

    if (totalSeconds > 0) {
      const hrs = Math.floor(totalSeconds / 3600);
      const mins = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;

      // Only update if different to avoid infinite loops
      const newHrs = hrs > 0 ? String(hrs) : '';
      const newMins = mins > 0 ? String(mins) : '';
      const newSecs = secs > 0 ? String(secs) : '';

      if (newHrs !== String(durationHours) || newMins !== String(durationMinutes) || newSecs !== String(durationSeconds)) {
        console.log('🏃 Auto-syncing workout duration from single cardio exercise:', { hrs, mins, secs });
        setDurationHours(newHrs);
        setDurationMinutes(newMins);
        setDurationSeconds(newSecs);
      }
    }
  }, [workoutExercises, durationHours, durationMinutes, durationSeconds]);

  // Auto-save draft whenever relevant state changes
  useEffect(() => {
    // Skip on initial mount to prevent clearing draft before it can be loaded
    if (isInitialMount.current) {
      console.log('⏸️ Auto-save skipped: initial mount');
      return;
    }

    // Don't save if showing draft prompt (user hasn't decided yet)
    if (showDraftPrompt) {
      console.log('⏸️ Auto-save skipped: draft prompt is showing');
      return;
    }

    // Don't save in edit mode
    if (isEditMode) {
      console.log('⏸️ Auto-save skipped: edit mode');
      return;
    }

    // Check for meaningful data
    const hasName = workoutName.trim().length > 0;
    const hasExercises = workoutExercises.length > 0;
    const hasMeaningfulData = hasName || hasExercises;

    // If workout is completely empty, clear any existing draft
    if (!hasMeaningfulData) {
      console.log('🗑️ Workout is empty - clearing any existing draft');
      clearDraft();
      return;
    }

    // Auto-save if workout has data
    console.log('✅ Auto-save triggered:', {
      workoutName,
      exerciseCount: workoutExercises.length
    });

    const workoutState = {
      workoutName,
      workoutDate,
      workoutTime,
      workoutNotes,
      durationHours,
      durationMinutes,
      durationSeconds,
      workoutExercises,
      programContext  // Include program context for draft restore
    };

    saveDraft(workoutState);
  }, [
    workoutName,
    workoutDate,
    workoutTime,
    workoutNotes,
    durationHours,
    durationMinutes,
    durationSeconds,
    workoutExercises,
    programContext,
    saveDraft,
    clearDraft,
    showDraftPrompt,
    isEditMode
  ]);

  // Add beforeunload handler to save draft when browser tab closes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Check if workout has meaningful data
      const hasData = workoutName.trim() || workoutExercises.length > 0;

      if (hasData && !showDraftPrompt && !isEditMode) {
        const workoutState = {
          workoutName,
          workoutDate,
          workoutTime,
          workoutNotes,
          durationHours,
          durationMinutes,
          durationSeconds,
          workoutExercises,
          programContext
        };

        // Try navigator.sendBeacon first (most reliable for beforeunload)
        const draftData = JSON.stringify(workoutState);
        const blob = new Blob([draftData], { type: 'application/json' });
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        // withPerson is required here: without it the server falls back to the
        // account's default person and this beacon would clobber their draft.
        const sent = navigator.sendBeacon(withPerson(`${API_URL}/api/workouts/draft`), blob);

        if (sent) {
          console.log('📡 Draft sent via beacon before unload');
        } else {
          console.warn('⚠️ Beacon failed, localStorage backup available');
        }

        // Note: We don't prevent unload, just save draft
        // If user really wants to leave, let them
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [workoutName, workoutDate, workoutTime, workoutNotes, durationHours, durationMinutes, durationSeconds, workoutExercises, programContext, showDraftPrompt, isEditMode]);

  const fetchExercises = async () => {
    setIsLoading(true);
    try {
      const data = await workoutAPI.fetchExercises();
      setExercises(data);
    } catch (err) {
      console.error('Error fetching exercises:', err);
      setError('Failed to load exercises');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate total minutes from manual HH:MM:SS inputs (as decimal to preserve seconds)
  const getManualDurationMinutes = () => {
    const hrs = parseInt(durationHours) || 0;
    const mins = parseInt(durationMinutes) || 0;
    const secs = parseInt(durationSeconds) || 0;
    if (hrs === 0 && mins === 0 && secs === 0) return null;
    // Return as decimal (e.g., 273.333 for 4hr 33min 20s) to preserve seconds precision
    return (hrs * 60) + mins + (secs / 60);
  };

  const handleAddExercise = () => {
    if (!selectedExercise) return;

    // Determine if exercise type uses sets
    const exerciseUsesSets = usesSets(selectedExercise.exercise_type);

    const newExercise = {
      type: 'exercise', // NEW: Support for different item types
      id: Date.now(), // Temporary ID for frontend
      exercise_id: selectedExercise.value,
      exercise_name: selectedExercise.label,
      exercise_type: selectedExercise.exercise_type,
      category: selectedExercise.category,
      notes: '',
      // AUTO-CREATE 1 SET for weight-based exercises
      sets: exerciseUsesSets ? [{
        set_number: 1,
        weight: '',
        weight_unit: 'lbs',
        resistance: isStretch(selectedExercise.exercise_type) ? '' : null,
        reps: hasReps(selectedExercise.exercise_type) ? '' : null,
        duration_seconds: hasDuration(selectedExercise.exercise_type) ? '' : null,
        notes: ''
      }] : [],
      cardio_data: null
    };

    setWorkoutExercises([...workoutExercises, newExercise]);
    setSelectedExercise(null);
    setShowAddExercise(false);
  };

  const handleCreateExercise = async (formData) => {
    setIsSavingExercise(true);
    try {
      const result = await workoutAPI.createExercise(formData);

      // Refresh exercises list
      await fetchExercises();

      // Auto-select the newly created exercise
      const newExercise = {
        value: result.id,
        label: formData.name,
        exercise_type: formData.exercise_type,
        category: formData.category
      };
      setSelectedExercise(newExercise);

      // Close exercise form modal
      setShowExerciseForm(false);
    } catch (err) {
      console.error('Error creating exercise:', err);
      setError('Failed to create exercise. Please try again.');
    } finally {
      setIsSavingExercise(false);
    }
  };

  const handleRemoveExercise = (exerciseId) => {
    const item = workoutExercises.find(ex => ex.id === exerciseId);

    let confirmMessage = '';
    if (item.type === 'superset') {
      confirmMessage = `Delete superset "${item.superset_name}"? This will remove all ${item.exercises.length} exercises.`;
    } else if (item.type === 'exercise') {
      const setCount = item.sets?.length || 0;
      confirmMessage = `Delete "${item.exercise_name}"?${setCount > 0 ? ` (${setCount} sets will be lost)` : ''}`;
    } else if (item.type === 'rest') {
      confirmMessage = 'Delete this rest block?';
    }

    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    setWorkoutExercises(workoutExercises.filter(ex => ex.id !== exerciseId));
  };

  const handleExerciseDataUpdate = (exerciseId, data) => {
    setWorkoutExercises(prevExercises =>
      prevExercises.map(ex =>
        ex.id === exerciseId ? { ...ex, ...data } : ex
      )
    );
  };

  // Drag-and-drop handlers for desktop reordering
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.classList.add('dragging');
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('dragging');
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      handleDragEnd(e);
      return;
    }

    const newExercises = [...workoutExercises];
    const [movedItem] = newExercises.splice(draggedIndex, 1);
    newExercises.splice(dropIndex, 0, movedItem);

    setWorkoutExercises(newExercises);
    handleDragEnd(e);
  };

  // Mobile button reordering handlers
  const handleMoveUp = (index) => {
    if (index === 0) return;

    const newExercises = [...workoutExercises];
    [newExercises[index - 1], newExercises[index]] =
    [newExercises[index], newExercises[index - 1]];

    setWorkoutExercises(newExercises);
  };

  const handleMoveDown = (index) => {
    if (index === workoutExercises.length - 1) return;

    const newExercises = [...workoutExercises];
    [newExercises[index], newExercises[index + 1]] =
    [newExercises[index + 1], newExercises[index]];

    setWorkoutExercises(newExercises);
  };

  // Rest block handler
  const handleAddRestBlock = () => {
    const newRestBlock = {
      type: 'rest',
      id: Date.now(),
      rest_duration_seconds: 90, // Default 90 seconds (1.5 minutes)
      rest_notes: ''
    };

    setWorkoutExercises([...workoutExercises, newRestBlock]);
  };

  // Superset handlers
  const handleCreateSuperset = () => {
    if (selectedExerciseIds.size < 2) {
      setError('Select at least 2 exercises to create a superset');
      return;
    }

    // Get selected exercises in current order (include exercises without type field for backward compatibility)
    const selectedExercises = workoutExercises.filter(ex =>
      selectedExerciseIds.has(ex.id)
    );

    // Validate none are existing supersets
    const hasInvalidItems = selectedExercises.some(ex =>
      ex.type === 'superset'
    );
    if (hasInvalidItems) {
      setError('Cannot create supersets from existing supersets');
      return;
    }

    // Ensure we got the exercises we expected
    if (selectedExercises.length !== selectedExerciseIds.size) {
      console.error('Mismatch in selected exercises:', {
        selectedIds: selectedExerciseIds.size,
        foundExercises: selectedExercises.length,
        selectedExercises
      });
      setError('Error selecting exercises. Please try again.');
      return;
    }

    // Create superset object
    const supersetName = `Superset: ${selectedExercises.map(ex =>
      ex.type === 'rest' ? 'Rest' : ex.exercise_name
    ).join(' + ')}`;
    const newSuperset = {
      type: 'superset',
      id: Date.now(),
      superset_name: supersetName,
      exercises: selectedExercises.map((ex, idx) => ({
        ...ex,
        type: ex.type || 'exercise', // Preserve existing type (rest or exercise)
        id: Date.now() + idx // New ID for nested exercise (use index to avoid duplicates)
      }))
    };

    console.log('Creating superset with exercises:', selectedExercises.map(e => e.exercise_name));

    // Remove individual exercises and insert superset at first position
    const firstSelectedIndex = workoutExercises.findIndex(ex =>
      selectedExerciseIds.has(ex.id)
    );

    const remainingExercises = workoutExercises.filter(ex =>
      !selectedExerciseIds.has(ex.id)
    );

    const updatedExercises = [
      ...remainingExercises.slice(0, firstSelectedIndex),
      newSuperset,
      ...remainingExercises.slice(firstSelectedIndex)
    ];

    console.log('Superset created successfully:', {
      supersetName: newSuperset.superset_name,
      exerciseCount: newSuperset.exercises.length,
      exerciseNames: newSuperset.exercises.map(e => e.exercise_name)
    });

    setWorkoutExercises(updatedExercises);
    setSelectedExerciseIds(new Set());
  };

  const handleBreakSuperset = (supersetId) => {
    const superset = workoutExercises.find(ex => ex.id === supersetId);
    if (!superset || superset.type !== 'superset') return;

    const supersetIndex = workoutExercises.findIndex(ex => ex.id === supersetId);

    // Break superset into individual exercises
    const individualExercises = superset.exercises.map(ex => ({
      ...ex,
      type: ex.type || 'exercise', // Preserve existing type (rest or exercise)
      id: Date.now() + Math.random()
    }));

    const updatedExercises = [
      ...workoutExercises.slice(0, supersetIndex),
      ...individualExercises,
      ...workoutExercises.slice(supersetIndex + 1)
    ];

    setWorkoutExercises(updatedExercises);
  };

  const handleSupersetExerciseUpdate = (supersetId, exerciseIndex, data) => {
    setWorkoutExercises(prevExercises =>
      prevExercises.map(item => {
        if (item.id === supersetId && item.type === 'superset') {
          const updatedExercises = [...item.exercises];
          updatedExercises[exerciseIndex] = {
            ...updatedExercises[exerciseIndex],
            ...data
          };
          return { ...item, exercises: updatedExercises };
        }
        return item;
      })
    );
  };

  const handleOpenSubstituteModal = (exerciseId, exercise_type, exercise_id, supersetId = null, subIndex = null) => {
    setSubstituteTarget({ exerciseId, supersetId, subIndex, exercise_type, exercise_id });
    setSubstituteSelection(null);
    setShowSubstituteModal(true);
  };

  const handleCloseSubstituteModal = () => {
    setShowSubstituteModal(false);
    setSubstituteTarget(null);
    setSubstituteSelection(null);
  };

  const handleConfirmSubstitute = () => {
    if (!substituteTarget || !substituteSelection) return;

    const newFields = {
      exercise_id: substituteSelection.value,
      exercise_name: substituteSelection.label,
      category: substituteSelection.category || null
    };

    if (substituteTarget.supersetId != null) {
      // Substitute within a superset — preserve sets/cardio_data/notes/template_notes
      setWorkoutExercises(prevExercises =>
        prevExercises.map(item => {
          if (item.id === substituteTarget.supersetId && item.type === 'superset') {
            const updatedExercises = [...item.exercises];
            updatedExercises[substituteTarget.subIndex] = {
              ...updatedExercises[substituteTarget.subIndex],
              ...newFields
            };
            return { ...item, exercises: updatedExercises };
          }
          return item;
        })
      );
    } else {
      // Substitute a standalone exercise in place
      setWorkoutExercises(prevExercises =>
        prevExercises.map(item =>
          item.id === substituteTarget.exerciseId
            ? { ...item, ...newFields }
            : item
        )
      );
    }

    handleCloseSubstituteModal();
  };

  const handleOpenNotesModal = async (exerciseId, currentNotes, exerciseName, exercise_id, supersetId = null, subIndex = null, template_notes = '') => {
    setNotesModalData({
      exerciseId,
      supersetId,
      subIndex,
      notes: currentNotes || '',
      template_notes: template_notes || '',
      exerciseName,
      exercise_id
    });

    // Fetch previous workout note
    if (exercise_id) {
      setLoadingPreviousNote(true);
      setPreviousNote(null);
      try {
        const lastNote = await workoutAPI.fetchLastWorkoutNote(exercise_id);
        setPreviousNote(lastNote);
      } catch (err) {
        console.error('Error fetching previous workout note:', err);
        // Don't show error to user, just continue without previous note
      } finally {
        setLoadingPreviousNote(false);
      }
    }

    setShowNotesModal(true);
  };

  const handleSaveNotes = () => {
    const { exerciseId, supersetId, subIndex, notes } = notesModalData;

    if (supersetId !== null && subIndex !== null) {
      // Update superset exercise notes
      handleSupersetExerciseUpdate(supersetId, subIndex, { notes });
    } else {
      // Update regular exercise notes
      handleExerciseDataUpdate(exerciseId, { notes });
    }

    setShowNotesModal(false);
    // Clear previous note after closing
    setPreviousNote(null);
  };

  const handleCloseNotesModal = () => {
    setShowNotesModal(false);
    // Clear previous note when modal closes
    setPreviousNote(null);
  };

  const validateWorkout = () => {
    if (!workoutName.trim()) {
      setError('Please enter a workout name');
      return false;
    }

    // Count actual exercises (not rest blocks)
    const exerciseCount = workoutExercises.filter(item =>
      item.type === 'exercise' || item.type === 'superset'
    ).length;

    if (exerciseCount === 0) {
      setError('Please add at least one exercise (rest blocks alone are not valid)');
      return false;
    }

    // Validate exercises and supersets
    for (const item of workoutExercises) {
      if (item.type === 'rest') {
        // Validate rest block
        if (item.rest_duration_seconds < 0) {
          setError('Rest block duration must be 0 or greater');
          return false;
        }
      } else if (item.type === 'superset') {
        // Validate each exercise in superset
        for (const exercise of item.exercises) {
          if (usesSets(exercise.exercise_type)) {
            if (!exercise.sets || exercise.sets.length === 0) {
              setError(`Superset "${item.superset_name}" - Exercise "${exercise.exercise_name}" needs at least 1 set`);
              return false;
            }
          } else if (exercise.exercise_type === 'cardio_basic') {
            if (!exercise.cardio_data || !exercise.cardio_data.duration_seconds) {
              setError(`Superset "${item.superset_name}" - Exercise "${exercise.exercise_name}" needs cardio data`);
              return false;
            }
          } else if (exercise.exercise_type === 'cardio_segments') {
            if (!exercise.cardio_segments || exercise.cardio_segments.length === 0) {
              setError(`Superset "${item.superset_name}" - Exercise "${exercise.exercise_name}" needs segments`);
              return false;
            }
          }
        }
      } else if (item.type === 'exercise') {
        // Validate regular exercise
        if (usesSets(item.exercise_type)) {
          if (!item.sets || item.sets.length === 0) {
            setError(`Please add sets for ${item.exercise_name}`);
            return false;
          }
        } else if (item.exercise_type === 'cardio_basic') {
          if (!item.cardio_data || !item.cardio_data.duration_seconds) {
            setError(`Please add cardio data for ${item.exercise_name}`);
            return false;
          }
        } else if (item.exercise_type === 'cardio_segments') {
          if (!item.cardio_segments || item.cardio_segments.length === 0) {
            setError(`Please add segments for ${item.exercise_name}`);
            return false;
          }
        }
      }
    }

    return true;
  };

  const handleCancel = async () => {
    const hasData = workoutExercises.length > 0 || workoutName.trim();

    if (hasData) {
      const confirmDiscard = window.confirm(
        'You have unsaved changes. Your draft will be saved automatically. Continue?'
      );
      if (!confirmDiscard) return;

      // Save immediately before navigating away
      const workoutState = {
        workoutName,
        workoutDate,
        workoutTime,
        workoutNotes,
        durationHours,
        durationMinutes,
        durationSeconds,
        workoutExercises,
        programContext
      };
      await saveDraftImmediately(workoutState);
      console.log('✅ Draft saved immediately before cancel');
    } else {
      // If workout is empty, clear any existing draft
      await clearDraft();
    }

    onCancel();
  };

  const handleClearWorkout = async () => {
    const hasData = workoutExercises.length > 0 || workoutName.trim();
    if (!hasData) return;

    const confirmed = window.confirm(
      'Are you sure you want to clear this entire workout? This will:\n\n' +
      '• Delete all exercises and sets\n' +
      '• Clear workout details\n' +
      '• Delete the saved draft\n\n' +
      'This action cannot be undone.'
    );

    if (!confirmed) return;

    // Reset all state
    setWorkoutName('');
    setWorkoutDate(new Date().toISOString().split('T')[0]);
    setWorkoutTime(new Date().toTimeString().split(' ')[0].substring(0, 5));
    setWorkoutNotes('');
    setDurationHours('');
    setDurationMinutes('');
    setDurationSeconds('');
    setWorkoutExercises([]);

    // Clear draft from server and localStorage
    await clearDraft();

    console.log('🗑️ Workout cleared completely');
  };

  const handleSaveWorkout = async () => {
    console.log('🚀 Save button clicked');
    console.log('📊 Current workout state:', {
      isEditMode,
      workoutName,
      workoutDate,
      workoutTime,
      exerciseCount: workoutExercises.length,
      exercises: workoutExercises
    });

    // Confirm before completing workout (only for new workouts, not edits)
    if (!isEditMode) {
      const confirmed = window.confirm(
        'Are you sure you want to complete this workout and clear the draft? Make sure you have logged all your exercises and sets.'
      );
      if (!confirmed) {
        console.log('⏸️ Workout completion cancelled by user');
        return;
      }
    }

    setError(null);

    if (!validateWorkout()) {
      console.log('❌ Validation failed');
      return;
    }

    console.log('✅ Validation passed, starting save...');
    setIsSaving(true);

    try {
      // If in edit mode, use the custom save handler
      if (isEditMode && onSaveWorkout) {
        const workoutData = {
          workoutId: initialData?.workoutId,
          workoutName,
          workoutDate,
          workoutTime,
          workoutNotes,
          durationMinutes: getManualDurationMinutes() || null,
          exercises: workoutExercises
        };

        console.log('✏️ Edit mode - calling onSaveWorkout with:', workoutData);
        await onSaveWorkout(workoutData);
        setIsSaving(false);
        return;
      }

      // Normal save logic for new workouts
      // Flatten superset structure for backend
      const flatExercises = [];
      let sortOrder = 0;

      workoutExercises.forEach((item) => {
        if (item.type === 'superset') {
          // Generate UUID for superset group
          const supersetId = crypto.randomUUID();
          item.exercises.forEach((ex, subIndex) => {
            if (ex.type === 'rest') {
              // Handle rest block in superset
              flatExercises.push({
                item_type: 'rest',
                sort_order: sortOrder++,
                superset_id: supersetId,
                superset_order: subIndex,
                rest_duration_seconds: ex.rest_duration_seconds,
                rest_notes: ex.rest_notes || null
              });
            } else {
              // Handle exercise in superset
              flatExercises.push({
                item_type: 'exercise',
                exercise_id: ex.exercise_id,
                sort_order: sortOrder++,
                superset_id: supersetId,
                superset_order: subIndex,
                notes: ex.notes || null,
                sets: ex.sets || [],
                cardio_data: ex.cardio_data || null,
                cardio_segments: ex.cardio_segments || []
              });
            }
          });
        } else if (item.type === 'rest') {
          flatExercises.push({
            item_type: 'rest',
            sort_order: sortOrder++,
            rest_duration_seconds: item.rest_duration_seconds,
            rest_notes: item.rest_notes || null
          });
        } else {
          // Regular exercise
          flatExercises.push({
            item_type: 'exercise',
            exercise_id: item.exercise_id,
            sort_order: sortOrder++,
            superset_id: null,
            superset_order: null,
            notes: item.notes || null,
            sets: item.sets || [],
            cardio_data: item.cardio_data || null,
            cardio_segments: item.cardio_segments || []
          });
        }
      });

      const workoutData = {
        workout_date: workoutDate,
        workout_time: workoutTime,
        name: workoutName,
        duration_minutes: getManualDurationMinutes() || null,
        notes: workoutNotes,
        template_id: template?.id || null,
        program_workout_id: programContext?.program_workout_id || null,
        exercises: flatExercises
      };

      console.log('📤 Sending workout data to API:', workoutData);
      const result = await workoutAPI.logWorkout(workoutData);
      console.log('✅ Workout saved successfully:', result);

      // If from a program, update progress
      if (programContext && result.success !== false) {
        try {
          await workoutAPI.updateProgramProgress(
            programContext.active_program_id,
            programContext.program_workout_id
          );
          console.log('✅ Program progress updated');
        } catch (progErr) {
          console.error('Error updating program progress:', progErr);
          // Don't fail the whole save if progress update fails
        }
      }

      // Clear the draft automatically after successful save
      await clearDraft();

      onFinish();
    } catch (err) {
      console.error('❌ Error saving workout:', err);
      setError('Failed to save workout. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Filter out archived exercises from workout logger
  const exerciseOptions = useMemo(() => {
    return exercises
      .filter(ex => ex.is_archived !== 1)
      .map(ex => ({
        value: ex.id,
        label: ex.name,
        exercise_type: ex.exercise_type,
        category: ex.category
      }));
  }, [exercises]);

  // Grouped for the picker so stretches sit in their own section rather than
  // interleaved alphabetically with lifts. Kept separate from the flat list
  // above, which other code filters over.
  const groupedExerciseOptions = useMemo(() => {
    const lifts = exerciseOptions.filter(opt => !isStretch(opt.exercise_type));
    const stretches = exerciseOptions.filter(opt => isStretch(opt.exercise_type));
    const groups = [];
    if (lifts.length > 0) groups.push({ label: 'Exercises', options: lifts });
    if (stretches.length > 0) groups.push({ label: 'Stretches', options: stretches });
    return groups;
  }, [exerciseOptions]);

  // Options for the substitute picker — same exercise_type only, excluding the current exercise
  const substituteOptions = useMemo(() => {
    if (!substituteTarget) return [];
    return exerciseOptions.filter(opt =>
      opt.exercise_type === substituteTarget.exercise_type
      && opt.value !== substituteTarget.exercise_id
    );
  }, [exerciseOptions, substituteTarget]);

  // Custom react-select components for "Create New Exercise" functionality
  const CustomMenuList = (props) => {
    return (
      <>
        <components.MenuList {...props}>
          {props.children}
        </components.MenuList>
        <div style={{ borderTop: '1px solid #ccc', padding: '8px' }}>
          <Button
            variant="outline-primary"
            size="sm"
            className="w-100"
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent blur/menu close
              e.stopPropagation();
              setShowExerciseForm(true);
            }}
            onTouchEnd={(e) => {
              e.preventDefault(); // Handle mobile touch
              e.stopPropagation();
              setShowExerciseForm(true);
            }}
          >
            <i className="fas fa-plus me-1"></i> Create New Exercise
          </Button>
        </div>
      </>
    );
  };

  const NoOptionsMessage = (props) => {
    return (
      <components.NoOptionsMessage {...props}>
        <div className="text-center py-2">
          <p className="mb-2">No exercises found</p>
          <small className="text-muted">Click "Create New Exercise" below to add it</small>
        </div>
      </components.NoOptionsMessage>
    );
  };

  const exerciseTypeIcons = {
    'weight_reps': 'fa-dumbbell',
    'weight_time': 'fa-clock',
    'cardio_basic': 'fa-running',
    'cardio_segments': 'fa-stopwatch'
  };

  // Helper function to truncate notes for preview
  const truncateNote = (note, maxLength = 40) => {
    if (!note || note.trim() === '') return '';
    const trimmed = note.trim();
    if (trimmed.length <= maxLength) return trimmed;
    return trimmed.substring(0, maxLength) + '...';
  };

  return (
    <div className="workout-logger">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>
            <i className="fas fa-clipboard-list me-2"></i>
            Log Workout
          </h2>
        </div>
        <div>
          <Button
            variant="outline-warning"
            onClick={handleClearWorkout}
            className="me-2"
            disabled={workoutExercises.length === 0 && !workoutName.trim()}
          >
            <i className="fas fa-eraser me-1"></i> Clear Workout
          </Button>
          <Button variant="outline-secondary" onClick={handleCancel} className="me-2">
            <i className="fas fa-times me-1"></i> Cancel
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Program Context */}
      {programContext && (
        <Alert variant="info" className="mb-3">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <strong>
                <i className="fas fa-calendar-week me-2"></i>
                Program: {programContext.program_name}
              </strong>
              <br />
              <span className="text-muted">
                {[
                  programContext.phase && `Phase ${programContext.phase}`,
                  programContext.week && `Week ${programContext.week}`,
                  programContext.day && `Day ${programContext.day}`
                ].filter(Boolean).join(', ')}
                {(programContext.phase || programContext.week || programContext.day) && ' - '}
                {programContext.name}
              </span>
            </div>
            {(programWorkout?.notes || programWorkout?.program_notes) && (
              <Button
                variant="outline-dark"
                size="sm"
                onClick={() => setShowProgramNotesModal(true)}
              >
                <i className="fas fa-sticky-note me-1"></i>
                Program Notes
              </Button>
            )}
          </div>
        </Alert>
      )}

      {/* Workout Details Card */}
      <Card className="mb-3">
        <Card.Header className="py-2 d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Workout Details</h6>
          {(programWorkout?.template || template)?.notes && (
            <Button
              variant="outline-info"
              size="sm"
              onClick={() => setShowTemplateNotesModal(true)}
            >
              <i className="fas fa-sticky-note me-1"></i>
              Template Notes
            </Button>
          )}
        </Card.Header>
        <Card.Body className="py-2">
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <Form.Group>
                <Form.Label className="mb-1 small">Workout Name *</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="e.g., Upper Body Strength"
                  value={workoutName}
                  onChange={(e) => setWorkoutName(e.target.value)}
                />
              </Form.Group>
            </div>
            <div className="col-6 col-md-2">
              <Form.Group>
                <Form.Label className="mb-1 small">Date</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={workoutDate}
                  onChange={(e) => setWorkoutDate(e.target.value)}
                />
              </Form.Group>
            </div>
            <div className="col-6 col-md-2">
              <Form.Group>
                <Form.Label className="mb-1 small">Time</Form.Label>
                <Form.Control
                  type="time"
                  size="sm"
                  value={workoutTime}
                  onChange={(e) => setWorkoutTime(e.target.value)}
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Label className="mb-1 small">Duration (HH:MM:SS)</Form.Label>
              <div className="d-flex align-items-center gap-1">
                <Form.Control
                  type="number"
                  size="sm"
                  min="0"
                  max="23"
                  placeholder="HH"
                  value={durationHours}
                  onChange={(e) => setDurationHours(e.target.value)}
                  style={{ width: '55px', textAlign: 'center' }}
                />
                <span className="text-muted">:</span>
                <Form.Control
                  type="number"
                  size="sm"
                  min="0"
                  max="59"
                  placeholder="MM"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(e.target.value)}
                  style={{ width: '55px', textAlign: 'center' }}
                />
                <span className="text-muted">:</span>
                <Form.Control
                  type="number"
                  size="sm"
                  min="0"
                  max="59"
                  placeholder="SS"
                  value={durationSeconds}
                  onChange={(e) => setDurationSeconds(e.target.value)}
                  style={{ width: '55px', textAlign: 'center' }}
                />
              </div>
            </div>
          </div>

          <div className="row g-2 mt-1">
            <div className="col-12">
              <Form.Group>
                <Form.Label className="mb-1 small">Workout Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  size="sm"
                  rows={1}
                  placeholder="How did you feel? Any observations?"
                  value={workoutNotes}
                  onChange={(e) => setWorkoutNotes(e.target.value)}
                />
              </Form.Group>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Exercises Section */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Exercises</h5>
          <div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowAddExercise(true)}
              className="me-2"
            >
              <i className="fas fa-plus me-1"></i> Add Exercise
            </Button>
            <Button
              variant="warning"
              size="sm"
              onClick={handleAddRestBlock}
              className="me-2"
            >
              <i className="fas fa-pause-circle me-1"></i> Add Rest Block
            </Button>
            {selectedExerciseIds.size >= 2 && (
              <Button
                variant="info"
                size="sm"
                onClick={handleCreateSuperset}
              >
                <i className="fas fa-layer-group me-1"></i>
                Create Superset ({selectedExerciseIds.size})
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {workoutExercises.length === 0 ? (
            <div className="text-center py-4">
              <i className="fas fa-dumbbell fa-3x text-muted mb-3"></i>
              <p className="text-muted">No exercises added yet. Click "Add Exercise" to get started.</p>
            </div>
          ) : (
            <div className="workout-exercises-list">
              {workoutExercises.map((item, index) => (
                <div key={item.id}>
                  {item.type === 'rest' ? (
                    // Render rest block
                    <Card
                      className={`mb-3 rest-block-wrapper ${dragOverIndex === index ? 'drag-over' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      onDrop={(e) => handleDrop(e, index)}
                    >
                      <Card.Header className="d-flex justify-content-between align-items-center bg-warning bg-opacity-25">
                        <div className="d-flex align-items-center gap-2">
                          {/* Drag handle for desktop */}
                          <span className="drag-handle d-none d-md-inline">
                            <i className="fas fa-grip-vertical"></i>
                          </span>

                          {/* Up/Down buttons for mobile */}
                          <div className="btn-group d-md-none" role="group">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleMoveUp(index)}
                              disabled={index === 0}
                            >
                              <i className="fas fa-arrow-up"></i>
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleMoveDown(index)}
                              disabled={index === workoutExercises.length - 1}
                            >
                              <i className="fas fa-arrow-down"></i>
                            </Button>
                          </div>
                        </div>

                        <div className="d-flex gap-2 align-items-center">
                          {/* Checkbox for superset selection */}
                          <Form.Check
                            type="checkbox"
                            checked={selectedExerciseIds.has(item.id)}
                            onChange={(e) => {
                              const newSelection = new Set(selectedExerciseIds);
                              if (e.target.checked) {
                                newSelection.add(item.id);
                              } else {
                                newSelection.delete(item.id);
                              }
                              setSelectedExerciseIds(newSelection);
                            }}
                            title="Select for superset"
                          />

                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveExercise(item.id)}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        <RestBlockCard
                          restBlock={item}
                          onUpdate={(data) => handleExerciseDataUpdate(item.id, data)}
                          onDelete={null}
                        />
                      </Card.Body>
                    </Card>
                  ) : item.type === 'superset' ? (
                    // Render superset
                    <Card
                      className={`mb-3 superset-card ${dragOverIndex === index ? 'drag-over' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      onDrop={(e) => handleDrop(e, index)}
                    >
                      <Card.Header className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          {/* Drag handle for desktop */}
                          <span className="drag-handle d-none d-md-inline text-white">
                            <i className="fas fa-grip-vertical"></i>
                          </span>

                          {/* Up/Down buttons for mobile */}
                          <div className="btn-group d-md-none" role="group">
                            <Button
                              variant="light"
                              size="sm"
                              onClick={() => handleMoveUp(index)}
                              disabled={index === 0}
                            >
                              <i className="fas fa-arrow-up"></i>
                            </Button>
                            <Button
                              variant="light"
                              size="sm"
                              onClick={() => handleMoveDown(index)}
                              disabled={index === workoutExercises.length - 1}
                            >
                              <i className="fas fa-arrow-down"></i>
                            </Button>
                          </div>

                          <div>
                            <h6 className="mb-0">
                              <i className="fas fa-layer-group me-2"></i>
                              {item.superset_name}
                            </h6>
                            <small>{item.exercises.length} exercises</small>
                          </div>
                        </div>

                        <div className="d-flex gap-2">
                          <Button
                            variant="light"
                            size="sm"
                            onClick={() => handleBreakSuperset(item.id)}
                          >
                            <i className="fas fa-unlink me-1"></i> Break Apart
                          </Button>
                          <Button
                            variant="light"
                            size="sm"
                            onClick={() => handleRemoveExercise(item.id)}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        {item.exercises.map((supersetEx, subIndex) => (
                          <div key={supersetEx.id} className="superset-exercise mb-4">
                            {supersetEx.type === 'rest' ? (
                              // Render rest block in superset
                              <div>
                                <div className="d-flex align-items-center mb-2">
                                  <Badge bg="warning" className="badge-superset me-2">
                                    {String.fromCharCode(65 + subIndex)}
                                  </Badge>
                                  <h6 className="mb-0">
                                    <i className="fas fa-pause-circle me-2"></i>
                                    Rest Block
                                  </h6>
                                </div>
                                <RestBlockCard
                                  restBlock={supersetEx}
                                  onUpdate={(data) => handleSupersetExerciseUpdate(item.id, subIndex, data)}
                                  onDelete={() => {
                                    // Remove this item from the superset
                                    const updatedExercises = item.exercises.filter((_, idx) => idx !== subIndex);
                                    if (updatedExercises.length < 2) {
                                      // If less than 2 items remain, break the superset
                                      handleBreakSuperset(item.id);
                                    } else {
                                      setWorkoutExercises(prevExercises =>
                                        prevExercises.map(ex =>
                                          ex.id === item.id ? { ...ex, exercises: updatedExercises } : ex
                                        )
                                      );
                                    }
                                  }}
                                />
                              </div>
                            ) : (
                              // Render exercise in superset
                              <>
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <div className="d-flex align-items-center">
                                    <Badge bg="info" className="badge-superset me-2">
                                      {String.fromCharCode(65 + subIndex)}
                                    </Badge>
                                    <h6 className="mb-0">{supersetEx.exercise_name}</h6>
                                    {supersetEx.category && (
                                      <Badge bg="secondary" className="ms-2">{supersetEx.category}</Badge>
                                    )}
                                  </div>
                                  <div className="d-flex align-items-center gap-1">
                                    {/* Template Notes Indicator */}
                                    {supersetEx.template_notes && (
                                      <Badge
                                        bg="info"
                                        style={{ cursor: 'pointer', fontSize: '0.7rem' }}
                                        onClick={() => handleOpenNotesModal(item.id, supersetEx.notes, supersetEx.exercise_name, supersetEx.exercise_id, item.id, subIndex, supersetEx.template_notes)}
                                        title="Template notes available - click to view"
                                      >
                                        <i className="fas fa-clipboard-list me-1"></i>
                                        Notes
                                      </Badge>
                                    )}
                                    {/* Exercise Notes Button with Preview */}
                                    <Button
                                      variant="link"
                                      size="sm"
                                      onClick={() => handleOpenNotesModal(item.id, supersetEx.notes, supersetEx.exercise_name, supersetEx.exercise_id, item.id, subIndex, supersetEx.template_notes)}
                                      className="p-0 text-secondary d-flex align-items-center"
                                      title={supersetEx.template_notes ? "Template notes available" : (supersetEx.notes || "Add notes")}
                                    >
                                      <i className={`fas fa-sticky-note${supersetEx.notes ? ' text-warning' : ''}`}></i>
                                      {supersetEx.notes && (
                                        <span className="ms-1 text-muted small" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {truncateNote(supersetEx.notes)}
                                        </span>
                                      )}
                                    </Button>
                                    {/* Substitute exercise button */}
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleOpenSubstituteModal(supersetEx.id, supersetEx.exercise_type, supersetEx.exercise_id, item.id, subIndex)}
                                      title="Substitute exercise"
                                    >
                                      <i className="fas fa-exchange-alt"></i>
                                    </Button>
                                  </div>
                                </div>

                                {/* Render SetLogger or CardioLogger for each exercise */}
                                {(usesSets(supersetEx.exercise_type)) && (
                                  <SetLogger
                                    exercise={supersetEx}
                                    onUpdate={(data) => handleSupersetExerciseUpdate(item.id, subIndex, data)}
                                  />
                                )}

                                {(supersetEx.exercise_type === 'cardio_basic' || supersetEx.exercise_type === 'cardio_segments') && (
                                  <CardioLogger
                                    exercise={supersetEx}
                                    onUpdate={(data) => handleSupersetExerciseUpdate(item.id, subIndex, data)}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  ) : (
                    // Render exercise
                    <Card
                      className={`mb-3 exercise-card ${dragOverIndex === index ? 'drag-over' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      onDrop={(e) => handleDrop(e, index)}
                    >
                      <Card.Header className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2 flex-grow-1">
                          {/* Drag handle for desktop */}
                          <span className="drag-handle d-none d-md-inline">
                            <i className="fas fa-grip-vertical"></i>
                          </span>

                          {/* Up/Down buttons for mobile */}
                          <div className="btn-group d-md-none" role="group">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleMoveUp(index)}
                              disabled={index === 0}
                            >
                              <i className="fas fa-arrow-up"></i>
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleMoveDown(index)}
                              disabled={index === workoutExercises.length - 1}
                            >
                              <i className="fas fa-arrow-down"></i>
                            </Button>
                          </div>

                          <div className="flex-grow-1">
                            <h6 className="mb-0">
                              <i className={`fas ${exerciseTypeIcons[item.exercise_type]} me-2`}></i>
                              {item.exercise_name}
                            </h6>
                            {item.category && (
                              <Badge bg="secondary" className="mt-1">{item.category}</Badge>
                            )}
                          </div>
                        </div>

                        <div className="d-flex gap-2 align-items-center">
                          {/* Template Notes Indicator */}
                          {item.template_notes && (
                            <Badge
                              bg="info"
                              className="me-1"
                              style={{ cursor: 'pointer', fontSize: '0.7rem' }}
                              onClick={() => handleOpenNotesModal(item.id, item.notes, item.exercise_name, item.exercise_id, null, null, item.template_notes)}
                              title="Template notes available - click to view"
                            >
                              <i className="fas fa-clipboard-list me-1"></i>
                              Notes
                            </Badge>
                          )}
                          {/* Exercise Notes Button with Preview */}
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => handleOpenNotesModal(item.id, item.notes, item.exercise_name, item.exercise_id, null, null, item.template_notes)}
                            className="p-0 text-secondary d-flex align-items-center"
                            title={item.template_notes ? "Template notes available" : (item.notes || "Add notes")}
                          >
                            <i className={`fas fa-sticky-note${item.notes ? ' text-warning' : ''}`}></i>
                            {item.notes && (
                              <span className="ms-1 text-muted small" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {truncateNote(item.notes)}
                              </span>
                            )}
                          </Button>
                          {/* Substitute exercise button */}
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleOpenSubstituteModal(item.id, item.exercise_type, item.exercise_id)}
                            title="Substitute exercise"
                          >
                            <i className="fas fa-exchange-alt"></i>
                            <span className="d-none d-sm-inline ms-1">Sub</span>
                          </Button>

                          {/* Checkbox for superset selection */}
                          <Form.Check
                            type="checkbox"
                            checked={selectedExerciseIds.has(item.id)}
                            onChange={(e) => {
                              const newSelection = new Set(selectedExerciseIds);
                              if (e.target.checked) {
                                newSelection.add(item.id);
                              } else {
                                newSelection.delete(item.id);
                              }
                              setSelectedExerciseIds(newSelection);
                            }}
                            title="Select for superset"
                          />

                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveExercise(item.id)}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        {/* Render appropriate logger based on exercise type */}
                        {(usesSets(item.exercise_type)) && (
                          <SetLogger
                            exercise={item}
                            onUpdate={(data) => handleExerciseDataUpdate(item.id, data)}
                          />
                        )}

                        {(item.exercise_type === 'cardio_basic' || item.exercise_type === 'cardio_segments') && (
                          <CardioLogger
                            exercise={item}
                            onUpdate={(data) => handleExerciseDataUpdate(item.id, data)}
                          />
                        )}
                      </Card.Body>
                    </Card>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Complete Workout Button */}
      <div className="d-flex justify-content-end gap-2">
        <Button
          variant="outline-secondary"
          onClick={handleCancel}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          variant="success"
          size="lg"
          onClick={handleSaveWorkout}
          disabled={isSaving || workoutExercises.length === 0}
        >
          {isSaving ? (
            <>
              <i className="fas fa-spinner fa-spin me-2"></i>
              {isEditMode ? 'Saving Changes...' : 'Completing Workout...'}
            </>
          ) : (
            <>
              <i className={`fas ${isEditMode ? 'fa-save' : 'fa-check-circle'} me-2`}></i>
              {isEditMode ? 'Save Changes' : 'Complete Workout'}
            </>
          )}
        </Button>
      </div>

      {/* Draft Restore Modal */}
      <Modal show={showDraftPrompt} onHide={() => setShowDraftPrompt(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Resume Draft?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You have an unfinished workout from {draftAge}.</p>
          <p>Would you like to resume where you left off?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-danger"
            onClick={async () => {
              await clearDraft();
              setShowDraftPrompt(false);
            }}
          >
            Discard Draft
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              const draft = await loadDraft();
              if (draft) {
                setWorkoutName(draft.workout_name || '');
                setWorkoutDate(
                  draft.workout_date
                    ? draft.workout_date.split('T')[0]  // Extract 'yyyy-MM-dd' from ISO datetime
                    : new Date().toISOString().split('T')[0]
                );
                setWorkoutTime(draft.workout_time || new Date().toTimeString().split(' ')[0].substring(0, 5));
                setWorkoutNotes(draft.workout_notes || '');
                // Restore duration - support both new HH:MM:SS format and legacy minutes-only
                if (draft.durationHours !== undefined || draft.durationMinutes !== undefined || draft.durationSeconds !== undefined) {
                  // New format
                  setDurationHours(draft.durationHours || '');
                  setDurationMinutes(draft.durationMinutes || '');
                  setDurationSeconds(draft.durationSeconds || '');
                } else if (draft.duration_minutes) {
                  // Legacy format - convert minutes (possibly decimal) to HH:MM:SS
                  const totalMins = parseFloat(draft.duration_minutes) || 0;
                  const totalSeconds = Math.round(totalMins * 60);
                  const hrs = Math.floor(totalSeconds / 3600);
                  const mins = Math.floor((totalSeconds % 3600) / 60);
                  const secs = totalSeconds % 60;
                  setDurationHours(hrs > 0 ? hrs : '');
                  setDurationMinutes(mins > 0 ? mins : '');
                  setDurationSeconds(secs > 0 ? secs : '');
                }

                // Migrate old draft exercises to have type field
                const migratedExercises = (draft.workoutExercises || []).map(ex => ({
                  ...ex,
                  type: ex.type || 'exercise' // Add type if missing (backward compatibility)
                }));
                setWorkoutExercises(migratedExercises);

                // Restore program context if present in draft
                if (draft.programContext) {
                  console.log('🔗 Restoring program context from draft:', draft.programContext);
                  setProgramContext(draft.programContext);
                } else {
                  console.log('ℹ️ No program context in draft (manual workout)');
                }

                console.log('Draft restored with exercises:', migratedExercises.map(e => ({
                  name: e.exercise_name || e.superset_name || 'Rest',
                  type: e.type
                })));
              }
              setShowDraftPrompt(false);
            }}
          >
            Resume Draft
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Substitute Exercise Modal */}
      <Modal show={showSubstituteModal} onHide={handleCloseSubstituteModal}>
        <Modal.Header closeButton>
          <Modal.Title>Substitute Exercise</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Pick a replacement</Form.Label>
            <Select
              options={substituteOptions}
              value={substituteSelection}
              onChange={setSubstituteSelection}
              placeholder="Search for exercise..."
              isSearchable
              isClearable
              components={{
                MenuList: CustomMenuList,
                NoOptionsMessage: NoOptionsMessage
              }}
            />
          </Form.Group>

          {substituteSelection && (
            <div className="mt-3">
              <p className="mb-1">
                <strong>Type:</strong> {substituteSelection.exercise_type.replace('_', ' ')}
              </p>
              {substituteSelection.category && (
                <p className="mb-0">
                  <strong>Category:</strong> {substituteSelection.category}
                </p>
              )}
            </div>
          )}

          <Alert variant="info" className="mt-3 mb-0 py-2 small">
            <i className="fas fa-info-circle me-1"></i>
            Logged sets and notes will be preserved.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseSubstituteModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmSubstitute}
            disabled={!substituteSelection}
          >
            Substitute
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Exercise Modal */}
      <Modal show={showAddExercise} onHide={() => setShowAddExercise(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Exercise</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Select Exercise</Form.Label>
            <Select
              options={groupedExerciseOptions}
              value={selectedExercise}
              onChange={setSelectedExercise}
              placeholder="Search for exercise..."
              isSearchable
              isClearable
              components={{
                MenuList: CustomMenuList,
                NoOptionsMessage: NoOptionsMessage
              }}
            />
          </Form.Group>

          {selectedExercise && (
            <div className="mt-3">
              <p className="mb-1">
                <strong>Type:</strong> {selectedExercise.exercise_type.replace('_', ' ')}
              </p>
              {selectedExercise.category && (
                <p className="mb-0">
                  <strong>Category:</strong> {selectedExercise.category}
                </p>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddExercise(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddExercise}
            disabled={!selectedExercise}
          >
            Add Exercise
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Exercise Modal */}
      <ExerciseForm
        show={showExerciseForm}
        onHide={() => setShowExerciseForm(false)}
        exercise={null}
        onSave={handleCreateExercise}
        isLoading={isSavingExercise}
      />

      {/* Exercise Notes Modal */}
      <Modal show={showNotesModal} onHide={handleCloseNotesModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Exercise Notes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            <strong>{notesModalData.exerciseName}</strong>
          </p>

          {/* Template Notes (Read-only) */}
          {notesModalData.template_notes && (
            <div className="mb-3 p-3 border rounded" style={{ backgroundColor: '#e8f4fd' }}>
              <div className="mb-2">
                <small className="text-muted">
                  <i className="fas fa-clipboard-list me-1"></i>
                  <strong>Template Notes:</strong>
                </small>
              </div>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                {notesModalData.template_notes}
              </div>
            </div>
          )}

          {/* Previous Workout Note (Read-only) */}
          {loadingPreviousNote ? (
            <div className="mb-3">
              <small className="text-muted">
                <i className="fas fa-spinner fa-spin me-1"></i>
                Loading previous note...
              </small>
            </div>
          ) : previousNote ? (
            <div className="mb-3 p-3 bg-light border rounded">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <small className="text-muted">
                  <i className="fas fa-history me-1"></i>
                  <strong>Last Session Notes:</strong>
                </small>
                <small className="text-muted">
                  {new Date(previousNote.workout_date).toLocaleDateString()}
                </small>
              </div>
              <div className="text-muted" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                {previousNote.notes}
              </div>
            </div>
          ) : null}

          {/* Current Note (Editable) */}
          <Form.Group>
            <Form.Label>
              <small className="text-muted">
                <i className="fas fa-edit me-1"></i>
                Your Notes (this session):
              </small>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Add notes for this exercise..."
              value={notesModalData.notes}
              onChange={(e) => setNotesModalData({ ...notesModalData, notes: e.target.value })}
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={handleCloseNotesModal}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSaveNotes}>
            Save Notes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Template Notes Modal (Read-only) */}
      <Modal show={showTemplateNotesModal} onHide={() => setShowTemplateNotesModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-clipboard-list me-2"></i>
            Template Notes
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2"><strong>{(programWorkout?.template || template)?.name}</strong></p>
          {(programWorkout?.template || template)?.description && (
            <div className="mb-3">
              <small className="text-muted">Description:</small>
              <p className="mb-0">{(programWorkout?.template || template).description}</p>
            </div>
          )}
          <div className="p-3 bg-light border rounded" style={{ whiteSpace: 'pre-wrap' }}>
            {(programWorkout?.template || template)?.notes || 'No notes available.'}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTemplateNotesModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Program Notes Modal (Read-only) */}
      <Modal show={showProgramNotesModal} onHide={() => setShowProgramNotesModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-calendar-alt me-2"></i>
            Program Notes
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2"><strong>{programContext?.program_name}</strong></p>

          {/* Program-level notes */}
          {programWorkout?.program_notes && (
            <div className="mb-3">
              <small className="text-muted d-block mb-1">Program Notes:</small>
              <div className="p-3 bg-light border rounded" style={{ whiteSpace: 'pre-wrap' }}>
                {programWorkout.program_notes}
              </div>
            </div>
          )}

          {/* Workout-specific notes */}
          {programWorkout?.notes && (
            <div>
              <small className="text-muted d-block mb-1">Workout Notes:</small>
              <div className="p-3 bg-light border rounded" style={{ whiteSpace: 'pre-wrap' }}>
                {programWorkout.notes}
              </div>
            </div>
          )}

          {!programWorkout?.program_notes && !programWorkout?.notes && (
            <p className="text-muted">No notes available.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowProgramNotesModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default WorkoutLogger;
